import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Alert,
  Spinner,
  Table,
} from "react-bootstrap";
import { Plus, Trash2, Play } from "react-feather";
import {
  IBasketTest,
  ICreateBasketTestRequest,
  SkuData,
  IBasketTestRunResult,
} from "../../interfaces";
import { usePost, usePut } from "../../api";
import { ResponsePreview } from "./response-preview";

interface BasketFormProps {
  show: boolean;
  onHide: () => void;
  existing?: IBasketTest | null;
  onSaved?: (basket: IBasketTest) => void;
}

const EMPTY_ITEM: SkuData = {
  quantity: 1,
  price: undefined,
  name: "",
  category: null,
  productUrl: null,
  productId: null,
};

function emptyForm(): ICreateBasketTestRequest {
  return {
    name: "",
    description: null,
    countryCode: "US",
    domain: "",
    partnerId: "",
    brandId: null,
    basketAmount: undefined,
    basketCurrency: "USD",
    deliveryAmount: undefined,
    subId: null,
    items: [{ ...EMPTY_ITEM }],
  };
}

function toForm(b: IBasketTest): ICreateBasketTestRequest {
  return {
    name: b.name,
    description: b.description ?? null,
    countryCode: b.countryCode,
    domain: b.domain,
    partnerId: b.partnerId,
    brandId: b.brandId ?? null,
    basketAmount: b.basketAmount,
    basketCurrency: b.basketCurrency,
    deliveryAmount: b.deliveryAmount,
    subId: b.subId ?? null,
    items: b.items.length > 0 ? b.items : [{ ...EMPTY_ITEM }],
  };
}

export const BasketFormModal: React.FC<BasketFormProps> = ({
  show,
  onHide,
  existing,
  onSaved,
}) => {
  const [form, setForm] = useState<ICreateBasketTestRequest>(emptyForm);
  const [runResult, setRunResult] = useState<IBasketTestRunResult | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  const createHook = usePost<IBasketTest, ICreateBasketTestRequest>();
  const updateHook = usePut<IBasketTest, ICreateBasketTestRequest>();
  const runHook = usePost<IBasketTestRunResult, ICreateBasketTestRequest>();

  const saving = createHook.loading || updateHook.loading;
  const running = runHook.loading;

  useEffect(() => {
    if (show) {
      setForm(existing ? toForm(existing) : emptyForm());
      setRunResult(null);
      setRunError(null);
    }
  }, [show, existing]);

  const set = <K extends keyof ICreateBasketTestRequest>(
    key: K,
    value: ICreateBasketTestRequest[K]
  ) => setForm((f) => ({ ...f, [key]: value }));

  const setItem = <K extends keyof SkuData>(
    idx: number,
    key: K,
    value: SkuData[K]
  ) =>
    setForm((f) => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [key]: value };
      return { ...f, items };
    });

  const addItem = () =>
    setForm((f) => ({ ...f, items: [...f.items, { ...EMPTY_ITEM }] }));

  const removeItem = (idx: number) =>
    setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    try {
      let result: IBasketTest;
      if (existing) {
        result = await updateHook.execute(
          `/basket-testing/${existing._id}`,
          form
        );
      } else {
        result = await createHook.execute("/basket-testing", form);
      }
      onSaved?.(result);
      onHide();
    } catch {
      // errors surfaced via hook.error
    }
  };

  const handleRun = async () => {
    setRunResult(null);
    setRunError(null);
    try {
      const result = await runHook.execute("/basket-testing/run", form);
      setRunResult(result);
    } catch (err: any) {
      setRunError(err?.data?.error ?? err?.message ?? "Unknown error");
    }
  };

  const saveError = createHook.error || updateHook.error;

  return (
    <Modal show={show} onHide={onHide} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          {existing ? "Edit Basket Test" : "New Basket Test"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {saveError && (
          <Alert variant="danger">
            {saveError.data?.error ?? saveError.message}
          </Alert>
        )}

        <Form>
          {/* ── Meta ─────────────────────────────────────────────────── */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Name *</Form.Label>
                <Form.Control
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="e.g. Nike US basket"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  value={form.description ?? ""}
                  onChange={(e) => set("description", e.target.value || null)}
                  placeholder="Optional"
                />
              </Form.Group>
            </Col>
          </Row>

          {/* ── Request fields ───────────────────────────────────────── */}
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Country Code *</Form.Label>
                <Form.Control
                  value={form.countryCode}
                  onChange={(e) =>
                    set("countryCode", e.target.value.toUpperCase())
                  }
                  placeholder="US"
                  maxLength={2}
                />
              </Form.Group>
            </Col>
            <Col md={5}>
              <Form.Group>
                <Form.Label>Domain *</Form.Label>
                <Form.Control
                  value={form.domain}
                  onChange={(e) => set("domain", e.target.value)}
                  placeholder="e.g. nike.com"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Partner ID *</Form.Label>
                <Form.Control
                  value={form.partnerId}
                  onChange={(e) => set("partnerId", e.target.value)}
                  placeholder="UUID"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Brand ID</Form.Label>
                <Form.Control
                  value={form.brandId ?? ""}
                  onChange={(e) => set("brandId", e.target.value || null)}
                  placeholder="null"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Sub ID</Form.Label>
                <Form.Control
                  value={form.subId ?? ""}
                  onChange={(e) => set("subId", e.target.value || null)}
                  placeholder="null"
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Basket Amount</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={form.basketAmount ?? ""}
                  onChange={(e) =>
                    set(
                      "basketAmount",
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Currency</Form.Label>
                <Form.Control
                  value={form.basketCurrency ?? ""}
                  onChange={(e) =>
                    set("basketCurrency", e.target.value || undefined)
                  }
                  placeholder="USD"
                  maxLength={3}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Delivery Amount</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={form.deliveryAmount ?? ""}
                  onChange={(e) =>
                    set(
                      "deliveryAmount",
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                />
              </Form.Group>
            </Col>
          </Row>

          {/* ── Items ────────────────────────────────────────────────── */}
          <div className="d-flex align-items-center justify-content-between mb-2">
            <strong>Items</strong>
            <Button variant="outline-secondary" size="sm" onClick={addItem}>
              <Plus size={14} className="me-1" />
              Add item
            </Button>
          </div>

          <Table bordered size="sm" responsive>
            <thead>
              <tr>
                <th>Name *</th>
                <th style={{ width: 80 }}>Qty *</th>
                <th style={{ width: 100 }}>Price</th>
                <th>Category</th>
                <th>Product ID</th>
                <th>Product URL</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {form.items.map((item, idx) => (
                <tr key={idx}>
                  <td>
                    <Form.Control
                      size="sm"
                      value={item.name}
                      onChange={(e) => setItem(idx, "name", e.target.value)}
                    />
                  </td>
                  <td>
                    <Form.Control
                      size="sm"
                      type="number"
                      min={1}
                      value={item.quantity}
                      onChange={(e) =>
                        setItem(idx, "quantity", Number(e.target.value))
                      }
                    />
                  </td>
                  <td>
                    <Form.Control
                      size="sm"
                      type="number"
                      step="0.01"
                      value={item.price ?? ""}
                      onChange={(e) =>
                        setItem(
                          idx,
                          "price",
                          e.target.value ? Number(e.target.value) : undefined
                        )
                      }
                    />
                  </td>
                  <td>
                    <Form.Control
                      size="sm"
                      value={item.category ?? ""}
                      onChange={(e) =>
                        setItem(idx, "category", e.target.value || null)
                      }
                    />
                  </td>
                  <td>
                    <Form.Control
                      size="sm"
                      value={item.productId ?? ""}
                      onChange={(e) =>
                        setItem(idx, "productId", e.target.value || null)
                      }
                    />
                  </td>
                  <td>
                    <Form.Control
                      size="sm"
                      value={item.productUrl ?? ""}
                      onChange={(e) =>
                        setItem(idx, "productUrl", e.target.value || null)
                      }
                    />
                  </td>
                  <td className="text-center">
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-danger"
                      onClick={() => removeItem(idx)}
                      disabled={form.items.length === 1}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Form>

        {/* ── Ad-hoc run result ─────────────────────────────────────── */}
        {runError && <Alert variant="danger">{runError}</Alert>}
        {runResult && (
          <div className="mt-3">
            <div className="d-flex align-items-center gap-2 mb-2">
              <strong>Run result</strong>
              <span
                className={`badge bg-${runResult.status === "success" ? "success" : "danger"}`}
              >
                {runResult.status}
              </span>
              <small className="text-muted">({runResult.durationMs}ms)</small>
            </div>
            <details className="mb-2">
              <summary className="text-muted small">Request sent</summary>
              <pre
                className="bg-light p-2 rounded small mt-1"
                style={{ maxHeight: 200, overflow: "auto" }}
              >
                {JSON.stringify(runResult.requestBody, null, 2)}
              </pre>
            </details>
            <ResponsePreview
              responseBody={runResult.responseBody}
              status={runResult.status}
              errorMessage={runResult.errorMessage}
            />
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="outline-primary"
          onClick={handleRun}
          disabled={running || saving}
        >
          {running ? (
            <Spinner size="sm" className="me-1" />
          ) : (
            <Play size={14} className="me-1" />
          )}
          Test (no save)
        </Button>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={saving || running}>
          {saving && <Spinner size="sm" className="me-1" />}
          {existing ? "Save changes" : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
